import { useCallback, useEffect, useRef, useState } from 'react';
import { isMobileNavOpen, scrollToPageTop } from './navSameRouteClick';

/** Ignore pull when the gesture starts inside open mobile nav or header chrome. */
function shouldBlockPullGesture(target) {
  if (!(target instanceof Element)) return false;
  if (isMobileNavOpen()) return true;
  return Boolean(target.closest('.site-header-fixed, .shop-mobile-nav__panel.show'));
}

/**
 * Mobile pull-to-refresh at scroll top. Calls onRefresh (async) when user pulls past threshold.
 */
export function usePullToRefresh(onRefresh, { enabled = true, threshold = 100 } = {}) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef(0);
  const trackingRef = useRef(false);
  const pullDistanceRef = useRef(0);
  const onRefreshRef = useRef(onRefresh);
  const refreshingRef = useRef(false);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    refreshingRef.current = refreshing;
  }, [refreshing]);

  const finishPull = useCallback(async () => {
    if (refreshingRef.current) return;
    setRefreshing(true);
    setPullDistance(0);
    pullDistanceRef.current = 0;
    trackingRef.current = false;
    try {
      await onRefreshRef.current?.();
      scrollToPageTop({ smooth: true });
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    if (!window.matchMedia('(pointer: coarse)').matches) return undefined;

    const onTouchStart = (e) => {
      if (window.scrollY > 2 || refreshingRef.current) return;
      if (shouldBlockPullGesture(e.target)) return;
      startYRef.current = e.touches[0]?.clientY ?? 0;
      trackingRef.current = true;
    };

    const onTouchMove = (e) => {
      if (!trackingRef.current || refreshingRef.current) return;
      if (window.scrollY > 2) {
        trackingRef.current = false;
        pullDistanceRef.current = 0;
        setPullDistance(0);
        return;
      }
      if (isMobileNavOpen() || shouldBlockPullGesture(e.target)) {
        trackingRef.current = false;
        pullDistanceRef.current = 0;
        setPullDistance(0);
        return;
      }
      const y = e.touches[0]?.clientY ?? 0;
      const delta = Math.max(0, y - startYRef.current);
      if (delta > 0 && window.scrollY <= 2) {
        const next = Math.min(delta, threshold * 1.35);
        pullDistanceRef.current = next;
        setPullDistance(next);
      }
    };

    const onTouchEnd = () => {
      if (!trackingRef.current) return;
      const shouldRefresh = pullDistanceRef.current >= threshold;
      trackingRef.current = false;
      pullDistanceRef.current = 0;
      setPullDistance(0);
      if (shouldRefresh) void finishPull();
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', onTouchEnd);

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [enabled, finishPull, threshold]);

  const pulling = pullDistance > 18;
  const ready = pullDistance >= threshold;

  return { pulling, ready, refreshing, pullDistance, threshold };
}
